"use client";

import StepHeading from "@/app/components/molecules/StepHeading";
import FormCard from "@/app/components/atoms/FormCard";
import DynamicForm from "@/app/components/organisms/DynamicForm";
import type { FormConfig } from "@/app/components/types";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emailConfig: FormConfig = {
  id: "register-email-form",
  fields: [
    {
      name: "email",
      label: "Enter your email address to get started",
      type: "email",
      placeholder: "Email Address",
      required: true,
      autoFocus: true,
    },
  ],
  submitLabel: "Continue",
  ariaLabelledBy: "register-header",
};

const validateEmail = (data: Record<string, string | boolean>) => {
  const errors: Record<string, string> = {};
  const email = String(data["email"] ?? "").trim();
  if (!EMAIL_REGEX.test(email)) {
    errors["email"] = "Please enter a valid email address";
  }
  return errors;
};

interface RegisterEmailStepProps {
  /** Called with the validated email when the user continues. */
  onContinue: (email: string) => void;
  /** Whether the email check is in progress. */
  loading?: boolean;
  /** Form-level error message (e.g. account already exists). */
  error?: string | null;
}

/**
 * @component RegisterEmailStep
 * @description Email entry step for the account registration flow. Prompts
 * the user for their email address before account creation. Calls the
 * validate-email API to determine the next step.
 * @param onContinue - Callback receiving the validated email when the user submits.
 * @param loading - Shows loading state on the submit button while checking email.
 * @param error - Form-level error message shown below the form.
 * @returns The registration email form.
 */
export default function RegisterEmailStep({
  onContinue,
  loading,
  error,
}: RegisterEmailStepProps) {
  const handleSubmit = (data: Record<string, string | boolean>) => {
    onContinue(String(data["email"] ?? "").trim());
  };

  return (
    <div className="flex flex-col gap-8">
      <StepHeading id="register-header" title="Create an account" />

      <div className="w-full max-w-md m-auto">
        <FormCard>
          <DynamicForm
            config={emailConfig}
            onSubmit={handleSubmit}
            validate={validateEmail}
            loading={loading}
          />
          {error && (
            <p role="alert" className="text-body-base text-destructive mt-2 text-center">
              {error}
            </p>
          )}
          <p className="text-center text-base text-charcoal">
            Already have an account?{" "}
            <a
              href="/login"
              className="underline hover:text-accent-green transition-colors"
            >
              Login here
            </a>
          </p>
        </FormCard>
      </div>
    </div>
  );
}
